import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Palette, Upload, Save, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useBranding } from '../contexts/BrandingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Preset themes for Super Admin
const presetThemes = [
  {
    name: 'Professional Dark',
    description: 'Modern & professional',
    preview: { sidebar: '#0f172a', header: '#6366f1', content: '#111827' },
    config: {
      sidebar_color: '#0f172a',
      sidebar_gradient_start: '#0f172a',
      sidebar_gradient_end: '#1e293b',
      sidebar_use_gradient: true,
      header_color: '#6366f1',
      header_gradient_start: '#6366f1',
      header_gradient_end: '#8b5cf6',
      header_use_gradient: true,
      content_bg_color: '#111827',
      content_bg_gradient_start: '#111827',
      content_bg_gradient_end: '#1f2937',
      content_use_gradient: true,
      primary_color: '#6366f1',
      secondary_color: '#22c55e'
    }
  },
  {
    name: 'Ocean Blue',
    description: 'Clean & trustworthy',
    preview: { sidebar: '#0c4a6e', header: '#0ea5e9', content: '#082f49' },
    config: {
      sidebar_color: '#0c4a6e',
      sidebar_gradient_start: '#0c4a6e',
      sidebar_gradient_end: '#164e63',
      sidebar_use_gradient: true,
      header_color: '#0ea5e9',
      header_gradient_start: '#0ea5e9',
      header_gradient_end: '#38bdf8',
      header_use_gradient: true,
      content_bg_color: '#082f49',
      content_bg_gradient_start: '#082f49',
      content_bg_gradient_end: '#0c4a6e',
      content_use_gradient: true,
      primary_color: '#0ea5e9',
      secondary_color: '#14b8a6'
    }
  },
  {
    name: 'Emerald Dark',
    description: 'Fresh & natural',
    preview: { sidebar: '#064e3b', header: '#10b981', content: '#022c22' },
    config: {
      sidebar_color: '#064e3b',
      sidebar_gradient_start: '#064e3b',
      sidebar_gradient_end: '#065f46',
      sidebar_use_gradient: true,
      header_color: '#10b981',
      header_gradient_start: '#10b981',
      header_gradient_end: '#34d399',
      header_use_gradient: true,
      content_bg_color: '#022c22',
      content_bg_gradient_start: '#022c22',
      content_bg_gradient_end: '#064e3b',
      content_use_gradient: true,
      primary_color: '#10b981',
      secondary_color: '#06b6d4'
    }
  },
  {
    name: 'Royal Purple',
    description: 'Elegant & premium',
    preview: { sidebar: '#3b0764', header: '#a855f7', content: '#1e1b4b' },
    config: {
      sidebar_color: '#3b0764',
      sidebar_gradient_start: '#3b0764',
      sidebar_gradient_end: '#581c87',
      sidebar_use_gradient: true,
      header_color: '#a855f7',
      header_gradient_start: '#a855f7',
      header_gradient_end: '#c084fc',
      header_use_gradient: true,
      content_bg_color: '#1e1b4b',
      content_bg_gradient_start: '#1e1b4b',
      content_bg_gradient_end: '#312e81',
      content_use_gradient: true,
      primary_color: '#a855f7',
      secondary_color: '#f472b6'
    }
  },
  {
    name: 'Slate Modern',
    description: 'Minimalist & clean',
    preview: { sidebar: '#1e293b', header: '#475569', content: '#0f172a' },
    config: {
      sidebar_color: '#1e293b',
      sidebar_gradient_start: '#1e293b',
      sidebar_gradient_end: '#334155',
      sidebar_use_gradient: true,
      header_color: '#475569',
      header_gradient_start: '#475569',
      header_gradient_end: '#64748b',
      header_use_gradient: true,
      content_bg_color: '#0f172a',
      content_bg_gradient_start: '#0f172a',
      content_bg_gradient_end: '#1e293b',
      content_use_gradient: true,
      primary_color: '#6366f1',
      secondary_color: '#22c55e'
    }
  },
  {
    name: 'Crimson Fire',
    description: 'Bold & energetic',
    preview: { sidebar: '#450a0a', header: '#dc2626', content: '#1c1917' },
    config: {
      sidebar_color: '#450a0a',
      sidebar_gradient_start: '#450a0a',
      sidebar_gradient_end: '#7f1d1d',
      sidebar_use_gradient: true,
      header_color: '#dc2626',
      header_gradient_start: '#dc2626',
      header_gradient_end: '#f87171',
      header_use_gradient: true,
      content_bg_color: '#1c1917',
      content_bg_gradient_start: '#1c1917',
      content_bg_gradient_end: '#292524',
      content_use_gradient: true,
      primary_color: '#dc2626',
      secondary_color: '#f59e0b'
    }
  }
];

const SuperAdminThemeSettings = () => {
  const { refreshBranding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    logo_url: null,
    logo_scale: 1.5,
    primary_color: '#6366f1',
    secondary_color: '#22c55e',
    company_name: 'RADIUSLINK',
    tagline: 'ISP Billing Management System',
    sidebar_color: '#0f172a',
    sidebar_gradient_start: '#0f172a',
    sidebar_gradient_end: '#1e293b',
    sidebar_use_gradient: true,
    content_bg_color: '#111827',
    content_bg_gradient_start: '#111827',
    content_bg_gradient_end: '#1f2937',
    content_use_gradient: true,
    header_color: '#6366f1',
    header_gradient_start: '#6366f1',
    header_gradient_end: '#8b5cf6',
    header_use_gradient: true
  });

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/superadmin/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setBranding(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
      toast.error('Gagal memuat pengaturan theme');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/v2/superadmin/branding`,
        branding,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Theme berhasil diperbarui! Memuat ulang...');
        // Refresh branding context first
        if (refreshBranding) {
          await refreshBranding();
        }
        // Then reload page to apply new branding
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan theme');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranding({ ...branding, logo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const applyPreset = (preset) => {
    setBranding(prev => ({ ...prev, ...preset.config }));
    toast.success(`Theme "${preset.name}" diterapkan! Klik Simpan untuk menyimpan.`);
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg">
        <CardTitle className="flex items-center gap-2 text-white">
          <Palette className="w-5 h-5" />
          Super Admin Theme Settings
        </CardTitle>
        <p className="text-sm text-white/80">
          Customize tampilan dashboard Super Admin
        </p>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Preset Themes */}
        <div>
          <Label className="text-white mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Tema Cepat (1 Klik)
          </Label>
          <p className="text-xs text-slate-400 mb-3">
            Pilih tema preset untuk mengubah semua warna sekaligus
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {presetThemes.map((preset, index) => (
              <button
                key={index}
                onClick={() => applyPreset(preset)}
                className="p-3 rounded-lg border border-slate-600 hover:border-indigo-500 transition-all text-left group"
              >
                <div className="flex gap-1 mb-2">
                  <div 
                    className="w-6 h-6 rounded" 
                    style={{ backgroundColor: preset.preview.sidebar }}
                  />
                  <div 
                    className="w-6 h-6 rounded" 
                    style={{ backgroundColor: preset.preview.header }}
                  />
                  <div 
                    className="w-6 h-6 rounded" 
                    style={{ backgroundColor: preset.preview.content }}
                  />
                </div>
                <p className="text-sm font-medium text-white group-hover:text-indigo-400">
                  {preset.name}
                </p>
                <p className="text-xs text-slate-400">{preset.description}</p>
              </button>
            ))}
          </div>
        </div>

        <hr className="border-slate-700" />

        {/* Logo Upload */}
        <div>
          <Label className="text-white mb-2 block">Logo Super Admin</Label>
          <div className="flex items-center gap-4">
            {branding.logo_url ? (
              <div className="relative">
                <img 
                  src={branding.logo_url} 
                  alt="Logo" 
                  className="h-16 w-auto rounded bg-slate-700 p-2"
                  style={{ transform: `scale(${branding.logo_scale || 1})` }}
                />
                <button
                  onClick={() => setBranding({ ...branding, logo_url: null })}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="h-16 w-32 rounded bg-slate-700 flex items-center justify-center text-slate-400 text-sm">
                No Logo
              </div>
            )}
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <div className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors">
                <Upload className="w-4 h-4" />
                Upload Logo
              </div>
            </label>
          </div>
          
          {/* Logo Scale Slider */}
          {branding.logo_url && (
            <div className="mt-4">
              <Label className="text-white mb-2 flex items-center justify-between">
                <span>Ukuran Logo</span>
                <span className="text-indigo-400 font-mono">{(branding.logo_scale || 1).toFixed(1)}x</span>
              </Label>
              <input
                type="range"
                min="0.5"
                max="2.5"
                step="0.1"
                value={branding.logo_scale || 1}
                onChange={(e) => setBranding({ ...branding, logo_scale: parseFloat(e.target.value) })}
                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>Kecil (0.5x)</span>
                <span>Normal (1x)</span>
                <span>Besar (2.5x)</span>
              </div>
            </div>
          )}
        </div>

        {/* Company Name & Tagline */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white mb-2 block">Nama Perusahaan</Label>
            <Input
              value={branding.company_name}
              onChange={(e) => setBranding({ ...branding, company_name: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2 block">Tagline</Label>
            <Input
              value={branding.tagline || ''}
              onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>
        </div>

        {/* Primary & Secondary Colors */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white mb-2 block">Warna Primer</Label>
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.primary_color}
                onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <Input
                value={branding.primary_color}
                onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white flex-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-white mb-2 block">Warna Sekunder</Label>
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.secondary_color}
                onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <Input
                value={branding.secondary_color}
                onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white flex-1"
              />
            </div>
          </div>
        </div>

        {/* Header Colors */}
        <div>
          <Label className="text-white mb-2 flex items-center gap-2">
            Header
            <Switch
              checked={branding.header_use_gradient}
              onCheckedChange={(checked) => setBranding({ ...branding, header_use_gradient: checked })}
            />
            <span className="text-xs text-slate-400">Gradient</span>
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.header_gradient_start}
                onChange={(e) => setBranding({ ...branding, header_gradient_start: e.target.value, header_color: e.target.value })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <Input
                value={branding.header_gradient_start}
                onChange={(e) => setBranding({ ...branding, header_gradient_start: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white flex-1"
                placeholder="Start"
              />
            </div>
            {branding.header_use_gradient && (
              <div className="flex gap-2">
                <input
                  type="color"
                  value={branding.header_gradient_end}
                  onChange={(e) => setBranding({ ...branding, header_gradient_end: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={branding.header_gradient_end}
                  onChange={(e) => setBranding({ ...branding, header_gradient_end: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white flex-1"
                  placeholder="End"
                />
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Colors */}
        <div>
          <Label className="text-white mb-2 flex items-center gap-2">
            Sidebar
            <Switch
              checked={branding.sidebar_use_gradient}
              onCheckedChange={(checked) => setBranding({ ...branding, sidebar_use_gradient: checked })}
            />
            <span className="text-xs text-slate-400">Gradient</span>
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.sidebar_gradient_start}
                onChange={(e) => setBranding({ ...branding, sidebar_gradient_start: e.target.value, sidebar_color: e.target.value })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <Input
                value={branding.sidebar_gradient_start}
                onChange={(e) => setBranding({ ...branding, sidebar_gradient_start: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white flex-1"
                placeholder="Start"
              />
            </div>
            {branding.sidebar_use_gradient && (
              <div className="flex gap-2">
                <input
                  type="color"
                  value={branding.sidebar_gradient_end}
                  onChange={(e) => setBranding({ ...branding, sidebar_gradient_end: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={branding.sidebar_gradient_end}
                  onChange={(e) => setBranding({ ...branding, sidebar_gradient_end: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white flex-1"
                  placeholder="End"
                />
              </div>
            )}
          </div>
        </div>

        {/* Content Background Colors */}
        <div>
          <Label className="text-white mb-2 flex items-center gap-2">
            Background Content
            <Switch
              checked={branding.content_use_gradient}
              onCheckedChange={(checked) => setBranding({ ...branding, content_use_gradient: checked })}
            />
            <span className="text-xs text-slate-400">Gradient</span>
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex gap-2">
              <input
                type="color"
                value={branding.content_bg_gradient_start}
                onChange={(e) => setBranding({ ...branding, content_bg_gradient_start: e.target.value, content_bg_color: e.target.value })}
                className="w-12 h-10 rounded cursor-pointer"
              />
              <Input
                value={branding.content_bg_gradient_start}
                onChange={(e) => setBranding({ ...branding, content_bg_gradient_start: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white flex-1"
                placeholder="Start"
              />
            </div>
            {branding.content_use_gradient && (
              <div className="flex gap-2">
                <input
                  type="color"
                  value={branding.content_bg_gradient_end}
                  onChange={(e) => setBranding({ ...branding, content_bg_gradient_end: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={branding.content_bg_gradient_end}
                  onChange={(e) => setBranding({ ...branding, content_bg_gradient_end: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white flex-1"
                  placeholder="End"
                />
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            onClick={fetchBranding}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Menyimpan...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Simpan Theme
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SuperAdminThemeSettings;
